import _slicedToArray from "/runner/_work/developers/developers/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React, { useContext, memo, useMemo, useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import OptimizelyService from "../../../services/optimizely-service";
import { jsx as _jsx } from "react/jsx-runtime";
var OptimizelyContext = /*#__PURE__*/React.createContext({
  experiments: {},
  userId: null,
  experimentsToActivate: null,
  activateExperiment: function activateExperiment() {},
  trackEvent: function trackEvent() {}
});
export var OPTIMIZELY_USER_COOKIE_ID = 'experiments-fingerprint';
export var OPTIMIZELY_EXPERIMENTS_COOKIE_ID = 'experiments-raw';
var useOptimizelyContext = function useOptimizelyContext() {
  return useContext(OptimizelyContext);
};
var OptimizelyProvider = function OptimizelyProvider(props) {
  var children = props.children,
    experiments = props.experiments,
    isTrackingEnabled = props.isTrackingEnabled,
    attributes = props.attributes,
    onExperimentActive = props.onExperimentActive,
    onExperimentActivationFailed = props.onExperimentActivationFailed;
  var optimizelyCallstack = useRef([]);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    userId = _useState2[0],
    setUserId = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    experimentsToActivate = _useState4[0],
    setExperimentsToActivate = _useState4[1];
  var activatedExperiments = useRef([]);
  if (!experiments) {
    throw new Error('OptimizelyProvider: experiments not provided.');
  }
  var trackActivation = useCallback(function (_ref) {
    var experiment = _ref.experiment,
      id = _ref.id;
    var campaignId = experiment.campaignId,
      experimentId = experiment.experimentId,
      variationId = experiment.variationId;
    if (activatedExperiments.current.includes(experimentId)) {
      return;
    }
    activatedExperiments.current.push(experimentId);
    OptimizelyService.activateExperiment({
      userId: id,
      campaignId: campaignId,
      experimentId: experimentId,
      variationId: variationId,
      attributes: attributes
    }).then(function (_ref2) {
      var ok = _ref2.ok,
        error = _ref2.error;
      ok ? onExperimentActive(experiment) : onExperimentActivationFailed({
        experiment: experiment,
        error: error
      });
    });
  }, [attributes, onExperimentActivationFailed, onExperimentActive]);
  var activateExperiment = useCallback(function (experiment) {
    var campaignId = experiment.campaignId,
      experimentId = experiment.experimentId,
      variationId = experiment.variationId;
    if (campaignId && experimentId && variationId) {
      if (isTrackingEnabled && userId) {
        trackActivation({
          experiment: experiment,
          id: userId
        });
      } else {
        var callstack = optimizelyCallstack.current;
        callstack.push(function (id) {
          trackActivation({
            experiment: experiment,
            id: id
          });
        });
      }
    }
  }, [isTrackingEnabled, userId, trackActivation]);
  var trackEvent = useCallback(function (eventId) {
    if (isTrackingEnabled && userId) {
      OptimizelyService.trackEvent({
        userId: userId,
        eventId: eventId,
        attributes: attributes
      });
    } else {
      var callstack = optimizelyCallstack.current;
      callstack.push(function (id) {
        return OptimizelyService.trackEvent({
          userId: id,
          eventId: eventId,
          attributes: attributes
        });
      });
    }
  }, [isTrackingEnabled, userId, attributes]);

  /* Manages the user ID storage */
  useEffect(function () {
    if (userId === undefined) {
      var cuid = Cookies.get(OPTIMIZELY_USER_COOKIE_ID);
      setUserId(cuid || null);
    }
  }, [userId]);

  /* Manages the experiments to activate storage */
  useEffect(function () {
    if (experimentsToActivate === undefined) {
      var experimentsRaw = Cookies.get(OPTIMIZELY_EXPERIMENTS_COOKIE_ID);
      setExperimentsToActivate(experimentsRaw ? OptimizelyService.normaliseExperiments(experimentsRaw) : null);
    }
  }, [experimentsToActivate]);

  /* Flushes the callstack client-side when the tracking service is enabled and the user ID available */
  useEffect(function () {
    if (isTrackingEnabled && userId) {
      var callstack = optimizelyCallstack.current;
      if (callstack.length > 0) {
        callstack.forEach(function (call) {
          return call(userId);
        });
        callstack.current = [];
      }
    }
  }, [isTrackingEnabled, userId]);
  var providerValue = useMemo(function () {
    return {
      experiments: experiments,
      userId: userId,
      experimentsToActivate: experimentsToActivate,
      activateExperiment: activateExperiment,
      trackEvent: trackEvent,
      attributes: attributes
    };
  }, [activateExperiment, experiments, experimentsToActivate, trackEvent, userId, attributes]);
  return /*#__PURE__*/_jsx(OptimizelyContext.Provider, {
    value: providerValue,
    children: children
  });
};
OptimizelyProvider.propTypes = {
  sdkKey: PropTypes.string,
  isTrackingEnabled: PropTypes.bool,
  experiments: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  attributes: PropTypes.array,
  onExperimentActive: PropTypes.func,
  onExperimentActivationFailed: PropTypes.func
};
OptimizelyProvider.defaultProps = {
  sdkKey: null,
  isTrackingEnabled: false,
  attributes: [],
  onExperimentActive: function onExperimentActive() {},
  onExperimentActivationFailed: function onExperimentActivationFailed() {}
};
OptimizelyProvider.displayName = 'OptimizelyProvider';
var MemoOptimizelyProvider = /*#__PURE__*/memo(OptimizelyProvider);
export { OptimizelyContext, useOptimizelyContext, MemoOptimizelyProvider as OptimizelyProvider };